import { useEffect, useRef, useState } from "react"

import type { SegmentPendingChange, SegmentPendingChangesPayload } from "../types"

import { Check, GitPullRequestArrow, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type SegmentPendingChangesProps = {
  canDecide: boolean
  pending: SegmentPendingChangesPayload | null
  /** True when the current user generated this proposal — surfaces a self-review note (§5.4). */
  runByYou?: boolean
  onDecide: (decision: "approve" | "reject", changeId?: string) => void
}

function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function changeOriginType(change: SegmentPendingChange): string | null {
  if (typeof change.origin === "string") return change.origin
  return change.origin?.type ?? null
}

/** Render a proposal value the way a person reads it — not as raw JSON. */
function DiffValue({ value }: { value: unknown }) {
  if (value == null || value === "") {
    return <span className="text-muted-foreground text-xs">—</span>
  }
  if (typeof value === "string") {
    return <p className="text-foreground text-xs leading-5 whitespace-pre-wrap">{value}</p>
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return <span className="text-foreground text-xs">{String(value)}</span>
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground text-xs">None</span>
    if (value.every((item) => typeof item === "string")) {
      return (
        <ul className="text-foreground list-disc space-y-0.5 pl-4 text-xs leading-5">
          {(value as string[]).map((item, index) => (
            <li key={`${item.slice(0, 24)}-${index}`}>{item}</li>
          ))}
        </ul>
      )
    }
    return (
      <ul className="space-y-1.5 text-xs">
        {value.map((item, index) => (
          <li key={index} className="border-border/50 border-l-2 pl-2">
            <DiffValue value={item} />
          </li>
        ))}
      </ul>
    )
  }
  if (isPlainObject(value)) {
    // Entity-shaped ({name, changeSummary/rationale/summary, flag}) → one readable line.
    if (typeof value.name === "string") {
      const detail = value.changeSummary ?? value.rationale ?? value.summary
      return (
        <p className="text-foreground text-xs leading-5">
          <span className="font-semibold">{value.name}</span>
          {value.flag ? (
            <span className="bg-primary/10 text-primary ml-1.5 rounded px-1 py-0.5 text-[10px] font-bold uppercase">
              {String(value.flag)}
            </span>
          ) : null}
          {detail ? <span className="text-muted-foreground"> — {String(detail)}</span> : null}
        </p>
      )
    }
    const entries = Object.entries(value).filter(([, v]) => v != null && v !== "")
    return (
      <dl className="grid gap-0.5 text-xs">
        {entries.map(([key, v]) => (
          <div key={key} className="flex flex-wrap gap-1">
            <dt className="text-muted-foreground">{humanizeKey(key)}:</dt>
            <dd className="text-foreground">
              {key.toLowerCase() === "sources" && Array.isArray(v) ? (
                <span className="text-muted-foreground">
                  {v.length} source{v.length === 1 ? "" : "s"}
                </span>
              ) : (
                <DiffValue value={v} />
              )}
            </dd>
          </div>
        ))}
      </dl>
    )
  }
  return <span className="text-foreground text-xs">{String(value)}</span>
}

export function SegmentPendingChanges({
  canDecide,
  pending,
  runByYou,
  onDecide,
}: SegmentPendingChangesProps) {
  const open = (pending?.changes ?? []).filter((change) => change.status === "pending")
  const openKey = open.map((change) => change.changeId).join(",")

  const prevOpenRef = useRef<SegmentPendingChange[]>(open)
  const exitTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const [exitingChanges, setExitingChanges] = useState<SegmentPendingChange[]>([])
  const [sectionFading, setSectionFading] = useState(false)

  // Bridge the flip from "pending" to decided: keep the row (and, if it was the
  // last one, the whole section) mounted for one more paint with an exit
  // transition instead of hard-removing it in the same render the status flips.
  // One timer per decided row — a later decision within the 200ms window must
  // not cancel an earlier row's pending removal.
  useEffect(() => {
    const prevOpen = prevOpenRef.current
    const openIds = new Set(open.map((change) => change.changeId))
    const decided = prevOpen.filter((change) => !openIds.has(change.changeId))
    prevOpenRef.current = open
    // New pending rows may arrive while the last-row exit fade is mid-flight;
    // un-fade immediately instead of waiting for the stale 200ms timer.
    if (open.length > 0) setSectionFading(false)
    if (decided.length === 0) return

    setExitingChanges((current) => [...current, ...decided])
    const isLastRowDecided = open.length === 0
    if (isLastRowDecided) setSectionFading(true)

    for (const change of decided) {
      const id = change.changeId
      const existing = exitTimersRef.current.get(id)
      if (existing) clearTimeout(existing)
      exitTimersRef.current.set(
        id,
        setTimeout(() => {
          exitTimersRef.current.delete(id)
          setExitingChanges((current) => current.filter((c) => c.changeId !== id))
          if (isLastRowDecided) setSectionFading(false)
        }, 200)
      )
    }
  }, [openKey])

  useEffect(() => {
    const timers = exitTimersRef.current
    return () => timers.forEach((timer) => clearTimeout(timer))
  }, [])

  if (open.length === 0 && exitingChanges.length === 0) return null

  const visibleChanges = [
    ...open,
    ...exitingChanges.filter((change) => !open.some((c) => c.changeId === change.changeId)),
  ]

  return (
    <section
      className={cn(
        "border-primary/30 bg-primary/[0.03] rounded-xl border p-4 shadow-sm transition-[opacity,scale] duration-200 ease-out sm:p-5",
        sectionFading && "pointer-events-none scale-[0.97] opacity-0"
      )}
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="bg-primary/10 text-primary flex size-8 items-center justify-center rounded-md">
            <GitPullRequestArrow className="size-4" />
          </span>
          <div>
            <h3 className="text-foreground text-sm font-semibold">
              {open.length} proposed change{open.length === 1 ? "" : "s"} awaiting approval
            </h3>
            <p className="text-muted-foreground text-xs">
              Agent output never goes live until a person confirms it.
            </p>
          </div>
        </div>
        {canDecide && runByYou ? (
          <p className="text-warning-foreground border-warning/30 bg-warning/10 w-full rounded-md border px-2.5 py-1.5 text-xs">
            You generated this proposal — approving it is self-review.
          </p>
        ) : null}
        {canDecide ? (
          <div className="flex gap-2">
            <Button className="font-semibold" size="sm" onClick={() => onDecide("approve")}>
              <Check className="size-3.5" />
              Approve all
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDecide("reject")}>
              <X className="size-3.5" />
              Reject all
            </Button>
          </div>
        ) : null}
      </header>

      <ul className="grid gap-3">
        {visibleChanges.map((change) => (
          <PendingChangeRow
            key={change.changeId}
            canDecide={canDecide}
            change={change}
            isExiting={exitingChanges.some((c) => c.changeId === change.changeId)}
            onDecide={onDecide}
          />
        ))}
      </ul>
    </section>
  )
}

function PendingChangeRow({
  canDecide,
  change,
  isExiting,
  onDecide,
}: {
  canDecide: boolean
  change: SegmentPendingChange
  isExiting: boolean
  onDecide: (decision: "approve" | "reject", changeId?: string) => void
}) {
  return (
    <li
      data-decided={isExiting || undefined}
      className={cn(
        "border-border bg-card rounded-lg border p-3 transition-[opacity,scale] duration-200 ease-out",
        isExiting && "pointer-events-none scale-[0.97] opacity-0"
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-foreground flex items-center gap-2 text-sm font-semibold">
          {change.label}
          {changeOriginType(change) === "company-rollup" ? (
            <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[10px] font-bold uppercase">
              from company analysis
            </span>
          ) : null}
          {changeOriginType(change) === "manual" ? (
            <span className="bg-warning/10 text-warning-foreground rounded px-1.5 py-0.5 text-[10px] font-bold uppercase">
              manual edit
            </span>
          ) : null}
        </span>
        {canDecide ? (
          <div className="flex shrink-0 gap-1.5">
            <Button
              aria-label={`Approve ${change.label}`}
              className="text-success hover:bg-success/10 hover:text-success size-8"
              size="icon"
              variant="ghost"
              onClick={() => onDecide("approve", change.changeId)}
            >
              <Check className="size-4" />
            </Button>
            <Button
              aria-label={`Reject ${change.label}`}
              className="text-muted-foreground hover:text-primary size-8"
              size="icon"
              variant="ghost"
              onClick={() => onDecide("reject", change.changeId)}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : null}
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <BeforeAfter label="Current" tone="muted" value={change.before} />
        <BeforeAfter label="Proposed" tone="primary" value={change.after} />
      </div>
    </li>
  )
}

function BeforeAfter({
  label,
  tone,
  value,
}: {
  label: string
  tone: "muted" | "primary"
  value: unknown
}) {
  return (
    <div
      className={cn(
        "rounded-md border p-2",
        tone === "primary" ? "border-primary/30 bg-primary/[0.04]" : "border-border bg-muted/40"
      )}
    >
      <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-[0.08em] uppercase">
        {label}
      </p>
      <div className="max-h-48 overflow-auto">
        <DiffValue value={value} />
      </div>
    </div>
  )
}
